import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import DiscordWidget from '../../components/DiscordWidget';
import '../Dashboard.css';
import './Module.css';

const Module2 = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();
  const [showQuiz, setShowQuiz] = useState(false);

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const totalProgress = progress?.totalProgress || 0;

  const quizQuestions = [
    {
      question: "Que signifie le 'C' dans la méthode CRISP ?",
      options: ["Créativité", "Contexte", "Clarté", "Concision"],
      correctAnswer: 1
    },
    {
      question: "Quelle est la meilleure façon d'améliorer une réponse de ChatGPT ?",
      options: [
        "Recommencer avec le même prompt",
        "Changer complètement de sujet",
        "Itérer en demandant des ajustements précis",
        "Utiliser des majuscules"
      ],
      correctAnswer: 2
    },
    {
      question: "Pourquoi donner un rôle à ChatGPT est efficace ?",
      options: [
        "Ça le rend plus intelligent",
        "Ça oriente ses réponses vers un style et une expertise spécifiques",
        "Ça accélère les réponses",
        "C'est juste pour s'amuser"
      ],
      correctAnswer: 1
    },
    {
      question: "Que faut-il faire si ChatGPT donne une réponse trop longue ?",
      options: [
        "Accepter la réponse telle quelle",
        "Recommencer depuis zéro",
        "Demander une version plus concise ou un résumé",
        "Ignorer la réponse"
      ],
      correctAnswer: 2
    },
    {
      question: "Dans la méthode CRISP, que représente le 'P' ?",
      options: ["Précision", "Personnalisation", "Prompt", "Paramètres"],
      correctAnswer: 1
    }
  ];

  const playgroundConfig = {
    title: "Générateur de Prompt CRISP",
    description: "Créez un prompt parfait en suivant la méthode CRISP",
    templatePrompt: `Tu es {{role}}.

CONTEXTE : {{context}}

TÂCHE : {{task}}

CONTRAINTES :
- Ton : {{tone}}
- Longueur : {{length}}
- Format : {{format}}

{{extra}}`,
    variables: [
      { name: 'role', label: 'Rôle de l\'IA', placeholder: 'Ex: expert en marketing digital', type: 'text', required: true },
      { name: 'context', label: 'Contexte (situation)', placeholder: 'Ex: Je lance une startup dans la foodtech...', type: 'textarea', required: true },
      { name: 'task', label: 'Tâche à accomplir', placeholder: 'Ex: Rédige 5 accroches pour ma landing page', type: 'text', required: true },
      { name: 'tone', label: 'Ton souhaité', type: 'select', options: [
        { value: 'professionnel', label: 'Professionnel' },
        { value: 'décontracté', label: 'Décontracté' },
        { value: 'inspirant', label: 'Inspirant' },
        { value: 'humoristique', label: 'Humoristique' }
      ], required: true },
      { name: 'length', label: 'Longueur', type: 'select', options: [
        { value: 'très court (1-2 phrases)', label: 'Très court' },
        { value: 'court (1 paragraphe)', label: 'Court' },
        { value: 'moyen (2-3 paragraphes)', label: 'Moyen' },
        { value: 'détaillé', label: 'Détaillé' }
      ], required: true },
      { name: 'format', label: 'Format de sortie', type: 'select', options: [
        { value: 'texte continu', label: 'Texte continu' },
        { value: 'liste à puces', label: 'Liste à puces' },
        { value: 'tableau', label: 'Tableau' },
        { value: 'étapes numérotées', label: 'Étapes numérotées' }
      ], required: true },
      { name: 'extra', label: 'Instructions supplémentaires (optionnel)', placeholder: 'Ex: Évite le jargon technique', type: 'text', required: false }
    ]
  };

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">MODULES</div>
          <div className="nav-item" onClick={() => navigate('/module/1')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></span>
            <span className="nav-text">1. Introduction IA</span>
          </div>
          <div className="nav-item active" onClick={() => navigate('/module/2')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="nav-text">2. ChatGPT</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/3')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
            <span className="nav-text">3. Claude Code</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/4')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg></span>
            <span className="nav-text">4. Automatisation</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/5')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            <span className="nav-text">5. Créer des Apps</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/6')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">6. Projets Avancés</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour aux programmes</button>
          <div className="module-badge">MODULE 2</div>
          <h1>🧠 Maîtriser ChatGPT</h1>
          <p className="module-subtitle">La méthode CRISP pour des prompts parfaits</p>
          <div style={{ marginTop: '20px', maxWidth: '400px' }}>
            <ProgressBar moduleId={2} showLabel={true} size="medium" />
          </div>
        </div>

        <div className="module-content">
          {/* Section 1 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>1. La méthode CRISP</h2>
              <SectionCheckbox moduleId={2} sectionId="section1" label="J'ai compris cette section" />
            </div>

            <p>
              CRISP est un framework simple pour structurer vos prompts et obtenir des réponses
              de qualité professionnelle à chaque fois.
            </p>

            <div className="principles-grid">
              <div className="principle-card">
                <div className="principle-number">C</div>
                <h4>Contexte</h4>
                <p>Qui êtes-vous ? Quelle est la situation ?</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">R</div>
                <h4>Rôle</h4>
                <p>Quel expert doit être l'IA ?</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">I</div>
                <h4>Instructions</h4>
                <p>Que voulez-vous exactement ?</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">S</div>
                <h4>Spécifications</h4>
                <p>Format, ton, longueur...</p>
              </div>
              <div className="principle-card">
                <div className="principle-number">P</div>
                <h4>Personnalisation</h4>
                <p>Ajustez selon vos besoins</p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>2. Le pouvoir du rôle</h2>
              <SectionCheckbox moduleId={2} sectionId="section2" label="J'ai compris cette section" />
            </div>

            <p>
              Donner un rôle à ChatGPT transforme complètement la qualité des réponses.
              L'IA va adopter le vocabulaire, l'expertise et le style de ce rôle.
            </p>

            <h3>Exemples de rôles puissants</h3>

            <PromptBox title="🎯 Rôle Expert Marketing">
{`Tu es un expert en marketing digital avec 15 ans d'expérience dans les startups SaaS B2B. Tu as travaillé avec des entreprises comme HubSpot et Salesforce. Tu connais parfaitement les stratégies d'acquisition et de conversion.`}
            </PromptBox>

            <PromptBox title="✍️ Rôle Copywriter">
{`Tu es un copywriter senior spécialisé dans la rédaction persuasive. Tu maîtrises les frameworks AIDA, PAS et les techniques de storytelling. Tu écris des textes qui convertissent.`}
            </PromptBox>

            <PromptBox title="💼 Rôle Consultant">
{`Tu es un consultant en stratégie d'entreprise. Tu analyses les situations de manière structurée, identifies les problèmes clés et proposes des solutions actionnables avec des métriques de succès.`}
            </PromptBox>

            <div className="tip-box">
              <div className="tip-icon">💡</div>
              <div className="tip-content">
                <h4>Astuce Pro</h4>
                <p>Plus le rôle est précis (années d'expérience, spécialité, références), plus les réponses seront pertinentes et expertes.</p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>3. L'art de l'itération</h2>
              <SectionCheckbox moduleId={2} sectionId="section3" label="J'ai compris cette section" />
            </div>

            <p>
              Ne vous arrêtez jamais à la première réponse. L'itération est la clé pour obtenir exactement ce que vous voulez.
            </p>

            <h3>Phrases magiques pour itérer</h3>

            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">🎯</div>
                <h4>Préciser</h4>
                <p>"Développe le point 3 avec des exemples concrets"</p>
              </div>
              <div className="info-card">
                <div className="card-icon">✂️</div>
                <h4>Raccourcir</h4>
                <p>"Résume en 3 bullet points maximum"</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🔄</div>
                <h4>Reformuler</h4>
                <p>"Reformule pour un public non-technique"</p>
              </div>
              <div className="info-card">
                <div className="card-icon">➕</div>
                <h4>Enrichir</h4>
                <p>"Ajoute des statistiques et sources"</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🎨</div>
                <h4>Changer le ton</h4>
                <p>"Rends ça plus engageant et dynamique"</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📋</div>
                <h4>Structurer</h4>
                <p>"Présente sous forme de tableau comparatif"</p>
              </div>
            </div>
          </section>

          {/* Section 4 - Playground */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>4. Pratiquez : Créez votre prompt CRISP</h2>
              <SectionCheckbox moduleId={2} sectionId="section4" label="J'ai fait l'exercice" />
            </div>

            <p>
              Utilisez cet outil pour créer un prompt parfait selon la méthode CRISP.
              Remplissez chaque champ et copiez le résultat pour l'utiliser dans ChatGPT.
            </p>

            <PromptPlayground
              title={playgroundConfig.title}
              description={playgroundConfig.description}
              templatePrompt={playgroundConfig.templatePrompt}
              variables={playgroundConfig.variables}
            />
          </section>

          {/* Section 5 */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>5. Prompts avancés par cas d'usage</h2>
              <SectionCheckbox moduleId={2} sectionId="section5" label="J'ai compris cette section" />
            </div>

            <h3>📧 Email professionnel</h3>
            <PromptBox title="Template Email">
{`Tu es un expert en communication professionnelle.

Rédige un email pour [SITUATION].

Destinataire : [QUI]
Objectif : [BUT DE L'EMAIL]
Ton : [FORMEL/DÉCONTRACTÉ]
Longueur : Maximum [X] mots

L'email doit :
- Avoir un objet accrocheur
- Aller droit au but
- Terminer par un call-to-action clair`}
            </PromptBox>

            <h3>📝 Contenu LinkedIn</h3>
            <PromptBox title="Template Post LinkedIn">
{`Tu es un expert en personal branding sur LinkedIn.

Crée un post viral sur [SUJET].

Structure :
- Hook accrocheur (première ligne qui donne envie de cliquer "voir plus")
- Histoire ou insight personnel
- 3-5 conseils actionnables
- Question engageante à la fin

Style : [INSPIRANT/ÉDUCATIF/STORYTELLING]
Utilise des émojis avec modération.
Ajoute des sauts de ligne pour la lisibilité.`}
            </PromptBox>

            <h3>📊 Analyse de données</h3>
            <PromptBox title="Template Analyse">
{`Tu es un data analyst senior.

Analyse ces données : [COLLER LES DONNÉES]

Je veux :
1. Les 3 insights principaux
2. Les tendances identifiées
3. Les anomalies ou points d'attention
4. Des recommandations actionnables

Présente sous forme de rapport structuré avec des bullet points.`}
            </PromptBox>
          </section>

          {/* Summary */}
          <section className="module-section">
            <h2>📝 Résumé du module</h2>
            <div className="summary-box">
              <ul>
                <li><strong>CRISP</strong> — Contexte, Rôle, Instructions, Spécifications, Personnalisation</li>
                <li><strong>Le rôle est crucial</strong> — Plus il est précis, meilleure est la réponse</li>
                <li><strong>Itérez toujours</strong> — La première réponse n'est que le début</li>
                <li><strong>Soyez spécifique</strong> — Ton, format, longueur, exemples</li>
                <li><strong>Gardez vos meilleurs prompts</strong> — Créez une bibliothèque personnelle</li>
              </ul>
            </div>
          </section>

          {/* Quiz */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur la méthode CRISP et l'utilisation de ChatGPT.</p>

            {!showQuiz ? (
              <button className="btn btn-primary" onClick={() => setShowQuiz(true)} style={{ marginTop: '16px' }}>
                🎯 Commencer le quiz
              </button>
            ) : (
              <Quiz moduleId={2} questions={quizQuestions} />
            )}
          </section>

          {/* Community */}
          <section className="module-section">
            <h2>💬 Besoin d'aide ?</h2>
            <p>Partagez vos meilleurs prompts et apprenez des autres membres.</p>
            <DiscordWidget inviteLink="https://discord.gg/votre-lien" />
          </section>

          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/module/1')}>← Module précédent</button>
            <button className="btn btn-primary" onClick={() => navigate('/module/3')}>Module suivant : Claude Code →</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Module2;
